import { forwardRef, useRef } from "react";
import style from "./ProxyPrices.module.scss";
import { Formik, FormikProps } from "formik";
import { Form } from "react-router-dom";
import { Button, Input } from "@ui";
import * as Yup from 'yup';
import cn from "classnames";
import { useGetAdminTariffsQuery, useUpdateTariffPricesMutation } from "@RTKQuery/admin/tariffs/tariffs";
import { ITariff } from "@interfaces/admin/tariff";

type TariffForm = {
    "3": string,
    "7": string,
    "30": string,
    "90": string
} & {
    [key: string]: string
};

const TARIFF_COLORS: { [id: number]: string } = {
    1: "bg-[#AAB3D3]",
    2: "bg-[#C6B164]",
    3: "bg-[#1E9EEE]",
};

export function ProxyPrices() {
    const { data } = useGetAdminTariffsQuery();
    const [updateTariffPrices, updateStatus] = useUpdateTariffPricesMutation();

    const tariffRefs = useRef<{ [id: number]: FormikProps<TariffForm> | null }>({});

    if (data && data.length > 0) {
        const handleSubmitAll = async () => {
            for (const tariff of data) {
                await tariffRefs.current[tariff.id]?.submitForm();
            }

            const allValid = data.every(t => tariffRefs.current[t.id]?.isValid);

            if (allValid) {
                for (const tariff of data) {
                    updateTariffPrices({
                        tariff_id: tariff.id,
                        data: tariff.rental_periods.map(item => ({
                            ...item,
                            price: Number(tariffRefs.current[tariff.id]?.values[String(item.rent_time)]) || 0
                        }))
                    });
                }
            }
        };

        return (
            <div className={style.container}>
                <div className={style.listTariffs}>
                    {data.map(tariff => (
                        <Tariff
                            key={tariff.id}
                            colorCircle={TARIFF_COLORS[tariff.id] ?? "bg-gray-400"}
                            data={tariff}
                            ref={el => { tariffRefs.current[tariff.id] = el; }}
                        />
                    ))}
                </div>
                <div className="bg-[#FFF] flex justify-center w-fit rounded-[14px] px-[30px] py-[35px] max-md:p-[15px]  max-md:w-full">
                    <Button
                        type="button"
                        text="Зберегти"
                        color="blue"
                        customStyle={{
                            button: "!w-[190px] !h-[40px]"
                        }}
                        onClick={() => {
                            handleSubmitAll()
                        }}
                        successContent={
                            updateStatus.isSuccess
                                ? {
                                    title: "Вітаємо!",
                                    description: <p className="max-w-[260px]">Ви успішно оновили всі ціни!</p>
                                }
                                : undefined
                        }
                        loading={updateStatus.isLoading}
                        errors={[
                            (updateStatus.error && ((extractError) => {
                                return {
                                    title: "От халепа!",
                                    description: <p className="max-w-[360px]">Схоже що деякі значення не вдалося оновити.</p>
                                }
                            }))
                        ]}
                        resetStatus={() => {
                            updateStatus.reset();
                        }}
                    />
                </div>
            </div>
        )
    } else {
        return null;
    }
}

const Tariff = forwardRef<FormikProps<TariffForm>, { colorCircle: string, data: ITariff }>(({
    colorCircle,
    data
}, ref) => {
    return (
        <div className={style.tariff}>
            <div className={style.header}>
                <div className="w-[20%] h-full bg-[#FFFFFF] rounded-ss-[25px] rounded-se-[8px]"></div>
                <div className="w-[60%] h-full bg-[#F3F4F8] p-[12px] pt-0 rounded-[10px]">
                    <div className="flex h-full justify-center items-center gap-[10px] bg-[#FFFFFF] rounded-[8px]">
                        <div className={cn("w-[15px] h-[15px] rounded-full", colorCircle)}></div>
                        <p className={style.tariffName}>{data.name}</p>
                    </div>
                </div>
                <div className="w-[20%] h-full bg-[#FFFFFF] rounded-ss-[8px] rounded-se-[25px]"></div>
            </div>
            <div className={style.body}>
                <Formik
                    initialValues={{
                        "3": String(data.rental_periods.find(item => item.rent_time === 3)?.price || ""),
                        "7": String(data.rental_periods.find(item => item.rent_time === 7)?.price || ""),
                        "30": String(data.rental_periods.find(item => item.rent_time === 30)?.price || ""),
                        "90": String(data.rental_periods.find(item => item.rent_time === 90)?.price || "")
                    }}
                    validationSchema={Yup.object({
                        "3": Yup.number().typeError("Тільки числа").required("Вартість - обов'язкове поле"),
                        "7": Yup.number().typeError("Тільки числа").required("Вартість - обов'язкове поле"),
                        "30": Yup.number().typeError("Тільки числа").required("Вартість - обов'язкове поле"),
                        "90": Yup.number().typeError("Тільки числа").required("Вартість - обов'язкове поле")
                    })}
                    innerRef={ref}
                    onSubmit={(values) => { }}
                >
                    {(props) => (
                        <Form>
                            <div className="flex flex-col gap-[28px]">
                                <div className="flex justify-between items-center">
                                    <div className={style.inputLabel}>Три дня:</div>
                                    <Input
                                        label="Вартість"
                                        type="text"
                                        onChange={(newValue) => {
                                            props.setFieldValue("3", newValue.trim())
                                        }}
                                        onBlur={props.handleBlur}
                                        value={String(props.values["3"])}
                                        name="3"
                                        customStyle={{
                                            width: "!max-w-[50%]"
                                        }}
                                        error={props.touched["3"] ? props.errors["3"] : ""}
                                    />
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className={style.inputLabel}>Тиждень:</div>
                                    <Input
                                        label="Вартість"
                                        type="text"
                                        onChange={(newValue) => {
                                            props.setFieldValue("7", newValue.trim())
                                        }}
                                        onBlur={props.handleBlur}
                                        value={String(props.values["7"])}
                                        name="7"
                                        customStyle={{
                                            width: "!max-w-[50%]"
                                        }}
                                        error={props.touched["7"] ? props.errors["7"] : ""}
                                    />
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className={style.inputLabel}>Місяць:</div>
                                    <Input
                                        label="Вартість"
                                        type="text"
                                        onChange={(newValue) => {
                                            props.setFieldValue("30", newValue.trim())
                                        }}
                                        onBlur={props.handleBlur}
                                        value={String(props.values["30"])}
                                        name="30"
                                        customStyle={{
                                            width: "!max-w-[50%]"
                                        }}
                                        error={props.touched["30"] ? props.errors["30"] : ""}
                                    />
                                </div>
                                <div className="flex justify-between items-center">
                                    <div className={style.inputLabel}>90 днів:</div>
                                    <Input
                                        label="Вартість"
                                        type="text"
                                        onChange={(newValue) => {
                                            props.setFieldValue("90", newValue.trim())
                                        }}
                                        onBlur={props.handleBlur}
                                        value={String(props.values["90"])}
                                        name="90"
                                        customStyle={{
                                            width: "!max-w-[50%]"
                                        }}
                                        error={props.touched["90"] ? props.errors["90"] : ""}
                                    />
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    )
})